import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * 声の選好順。ブラウザ既定の音声は機械的に聞こえることが多いので、
 * 入手できる中で最も自然な英語音声を選ぶ。
 *   1. Chrome のネットワーク音声（Google US English）
 *   2. macOS / iOS の Premium・Enhanced 音声
 *   3. 定評のあるシステム音声（Ava / Samantha など）
 *   4. Windows のニューラル音声（Aria / Jenny など）
 */
const VOICE_RANK: RegExp[] = [
  /google (us|uk) english/i,
  /\((premium|enhanced|neural)\)/i,
  /\b(ava|samantha|allison|serena|daniel)\b/i,
  /microsoft (aria|jenny|guy|michelle|ryan|sonia)/i,
];

/**
 * macOS のノベルティ音声（en-US を名乗るが人間の発音ではない）。
 * 日本語ロケールでは表示名も日本語になるため、両方の名前を弾く。
 * ブラウザ既定がこの中の音声（例: Albert）になっている環境があるので必ず除外する。
 */
const NOVELTY_VOICES =
  /^(albert|bad news|bahh|bells|boing|bubbles|cellos|good news|jester|organ|superstar|trinoids|whisper|wobble|zarvox|grandma|grandpa|rocko|shelley|sandy|flo|eddy|reed|junior|kathy|princess|deranged|hysterical|pipe organ|,|悪い知らせ|良い知らせ|ベル|道化|オルガン|パイプオルガン|スーパースター|トリノイド|ささやき声|震え|泡|チェロ|うなり|鐘)/i;

const voiceCache = new Map<string, SpeechSynthesisVoice | null>();

if (typeof window !== 'undefined' && window.speechSynthesis) {
  // 音声リストは非同期に届くことがある。届いたらキャッシュを捨てて選び直す。
  window.speechSynthesis.addEventListener?.('voiceschanged', () => voiceCache.clear());
  // 初回の getVoices() で読み込みを促しておく
  window.speechSynthesis.getVoices();
}

function normalizeLang(lang: string): string {
  return lang.toLowerCase().replace('_', '-');
}

function pickVoice(lang: string): SpeechSynthesisVoice | null {
  const cached = voiceCache.get(lang);
  if (cached !== undefined) return cached;

  const all = window.speechSynthesis.getVoices();
  if (all.length === 0) return null; // まだ読み込み中。キャッシュせず次回に回す。

  const target = normalizeLang(lang);
  const base = target.slice(0, 2);
  // その言語の音声だけを対象にする。日本語音声が英語を読む（カタカナ発音）事故を防ぐ。
  const sameLanguage = all.filter(
    (v) => normalizeLang(v.lang).startsWith(base) && !NOVELTY_VOICES.test(v.name),
  );
  if (sameLanguage.length === 0) {
    voiceCache.set(lang, null);
    return null;
  }

  const exact = sameLanguage.filter((v) => normalizeLang(v.lang) === target);
  const pool = exact.length > 0 ? exact : sameLanguage;

  const picked =
    VOICE_RANK.reduce<SpeechSynthesisVoice | null>(
      (found, rx) => found ?? pool.find((v) => rx.test(v.name)) ?? null,
      null,
    ) ??
    pool.find((v) => v.default) ??
    pool[0];

  voiceCache.set(lang, picked);
  return picked;
}

export function useAudio() {
  const [isPlaying, setIsPlaying] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (typeof window !== 'undefined') {
        window.speechSynthesis?.cancel();
      }
    };
  }, []);

  const speak = useCallback((text: string, lang = 'en-US', rate = 1) => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;

    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang;
    utterance.rate = rate;
    utterance.pitch = 1;

    const voice = pickVoice(lang);
    if (voice) utterance.voice = voice;

    utterance.onstart = () => {
      if (mountedRef.current) setIsPlaying(true);
    };
    utterance.onend = () => {
      if (mountedRef.current) setIsPlaying(false);
    };
    utterance.onerror = () => {
      if (mountedRef.current) setIsPlaying(false);
    };

    window.speechSynthesis.speak(utterance);
  }, []);

  const stop = useCallback(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (mountedRef.current) setIsPlaying(false);
  }, []);

  return { speak, stop, isPlaying };
}
